import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Star,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Award,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  MessageSquare,
  FolderOpen,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import AdminMediaLibrary from './AdminMediaLibrary';
import { toRelativeMediaPath } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SOURCE_OPTIONS = [
  { value: 'google', label: 'Google' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'manual', label: 'Manuell' }
];

const AdminReviews = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    author: '',
    avatar_url: '',
    rating: 5,
    text: '',
    date_display: '',
    source: 'google',
    verified: true,
    featured: false,
    published: true,
    service_category: ''
  });

  // Handle media selection from library
  const handleMediaSelect = (media) => {
    // Use the medium version for avatar if available, otherwise original
    const imageUrl = toRelativeMediaPath(media.versions?.medium || media.url || `/media/${media.filename}`);
    setFormData({ ...formData, avatar_url: imageUrl });
    setShowMediaPicker(false);
    toast({
      title: 'Bilde valgt',
      description: `${media.title || media.filename} er valgt som profilbilde`
    });
  };

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reviews/admin`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reviews/stats`)
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Feil ved henting av anmeldelser:', error);
      toast({
        title: 'Feil',
        description: 'Kunne ikke hente anmeldelser',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         review.text.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || review.service_category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const resetForm = () => {
    setFormData({
      author: '',
      avatar_url: '',
      rating: 5,
      text: '',
      date_display: 'Nylig',
      source: 'google',
      verified: true,
      featured: false,
      published: true,
      service_category: ''
    });
  };

  const handleCreate = async () => {
    if (!formData.author || !formData.text) {
      toast({
        title: 'Feil',
        description: 'Fyll ut navn og anmeldelse',
        variant: 'destructive'
      });
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/reviews`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Anmeldelse opprettet! ✅',
        description: `Anmeldelse fra ${formData.author} er lagt til`
      });
      
      setCreateModalOpen(false);
      resetForm();
      fetchReviews();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke opprette anmeldelse',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedReview) return;

    setActionLoading(true);
    try {
      await axios.put(`${API}/reviews/${selectedReview.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Anmeldelse oppdatert! ✅',
        description: `Anmeldelse fra ${formData.author} er oppdatert`
      });
      
      setEditModalOpen(false);
      setSelectedReview(null);
      fetchReviews();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke oppdatere anmeldelse',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedReview) return;

    setActionLoading(true);
    try {
      await axios.delete(`${API}/reviews/${selectedReview.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Anmeldelse slettet',
        description: `Anmeldelse fra ${selectedReview.author} er fjernet`
      });
      
      setDeleteModalOpen(false);
      setSelectedReview(null);
      fetchReviews();
    } catch (error) {
      toast({
        title: 'Feil',
        description: error.response?.data?.detail || 'Kunne ikke slette anmeldelse',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleFeatured = async (review) => {
    try {
      await axios.post(`${API}/reviews/${review.id}/toggle-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: review.featured ? 'Fjernet fra utvalgte' : 'Lagt til utvalgte',
        description: `${review.author}s anmeldelse er ${review.featured ? 'fjernet fra' : 'lagt til'} utvalgte`
      });
      
      fetchReviews();
    } catch (error) {
      toast({
        title: 'Feil',
        description: 'Kunne ikke endre status',
        variant: 'destructive'
      });
    }
  };

  const handleTogglePublished = async (review) => {
    try {
      await axios.post(`${API}/reviews/${review.id}/toggle-published`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: review.published ? 'Skjult' : 'Publisert',
        description: `Anmeldelse er nå ${review.published ? 'skjult' : 'synlig'}`
      });
      
      fetchReviews();
    } catch (error) {
      toast({
        title: 'Feil',
        description: 'Kunne ikke endre status',
        variant: 'destructive'
      });
    }
  };

  const openEditModal = (review) => {
    setSelectedReview(review);
    setFormData({
      author: review.author,
      avatar_url: review.avatar_url || '',
      rating: review.rating,
      text: review.text,
      date_display: review.date_display || '',
      source: review.source || 'google',
      verified: review.verified,
      featured: review.featured,
      published: review.published,
      service_category: review.service_category || ''
    });
    setEditModalOpen(true);
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${interactive ? 'cursor-pointer' : ''} ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-600 text-gray-600'
            }`}
            onClick={() => interactive && onChange && onChange(star)}
          />
        ))}
      </div>
    );
  };

  // Field update handler - prevents full re-render on each keystroke
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-yellow-900/50 to-yellow-800/30 border-yellow-700">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{stats.average_rating}</p>
                <div className="flex justify-center my-2">
                  {renderStars(Math.round(stats.average_rating))}
                </div>
                <p className="text-gray-400 text-sm">Snittkarakter</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-700">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-white">{stats.total_reviews}</p>
              <p className="text-gray-400 text-sm">Totalt</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-700">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-white">{stats.published_count}</p>
              <p className="text-gray-400 text-sm">Publisert</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 border-purple-700">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-white">{stats.featured_count}</p>
              <p className="text-gray-400 text-sm">Utvalgte</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-900/50 to-orange-800/30 border-orange-700">
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-white">{stats.rating_breakdown['5']}</p>
              <p className="text-gray-400 text-sm">5-stjerner</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-yellow-400" />
              Anmeldelser
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Søk..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-600 text-white w-48"
                />
              </div>
              <Select value={filterCategory || undefined} onValueChange={(val) => setFilterCategory(val === "all" ? "" : val)}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="all" className="text-white">
                    Alle kategorier
                  </SelectItem>
                  <SelectItem value="tungbil" className="text-white">
                    Tungbil
                  </SelectItem>
                  <SelectItem value="tilhenger" className="text-white">
                    Tilhenger
                  </SelectItem>
                  <SelectItem value="personbil" className="text-white">
                    Personbil
                  </SelectItem>
                  <SelectItem value="service" className="text-white">
                    Service
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={() => {
                  resetForm();
                  setCreateModalOpen(true);
                }}
                className="bg-yellow-600 hover:bg-yellow-700"
                data-testid="create-review-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ny anmeldelse
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Ingen anmeldelser funnet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReviews.map((review) => (
                <div
                  key={review.id}
                  className={`p-4 rounded-xl border transition-all ${
                    !review.published
                      ? 'bg-gray-900/50 border-gray-700 opacity-60'
                      : review.featured
                      ? 'bg-yellow-900/20 border-yellow-600/50'
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {review.avatar_url ? (
                        <img 
                          src={review.avatar_url} 
                          alt={review.author}
                          className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-12 h-12 bg-gradient-to-br from-red-600 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg ${review.avatar_url ? 'hidden' : ''}`}
                      >
                        {review.author.charAt(0)}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-medium">{review.author}</h3>
                          {review.verified && (
                            <Badge className="bg-green-600 text-xs">Verifisert</Badge>
                          )}
                          {review.featured && (
                            <Badge className="bg-yellow-600 text-xs">Utvalgt</Badge>
                          )}
                          {!review.published && (
                            <Badge className="bg-gray-600 text-xs">Skjult</Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1">
                          {renderStars(review.rating)}
                          <span className="text-gray-500 text-sm">{review.date_display}</span>
                          <Badge variant="outline" className="border-gray-600 text-gray-400 text-xs">
                            {review.source}
                          </Badge>
                          {review.service_category && (
                            <Badge variant="outline" className="border-gray-600 text-gray-400 text-xs capitalize">
                              {review.service_category}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-gray-300 mt-2 line-clamp-2">{review.text}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`border-yellow-600 ${review.featured ? 'bg-yellow-600/20 text-yellow-400' : 'text-yellow-400'} hover:bg-yellow-600/20`}
                        onClick={() => handleToggleFeatured(review)}
                        title={review.featured ? 'Fjern fra utvalgte' : 'Legg til utvalgte'}
                      >
                        <Award className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className={`${review.published ? 'border-green-600 text-green-400' : 'border-gray-600 text-gray-400'} hover:bg-gray-700`}
                        onClick={() => handleTogglePublished(review)}
                        title={review.published ? 'Skjul' : 'Publiser'}
                      >
                        {review.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        onClick={() => openEditModal(review)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-600 text-red-400 hover:bg-red-600/20"
                        onClick={() => {
                          setSelectedReview(review);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-yellow-400" />
              Ny anmeldelse
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Legg til en ny kundeanmeldelse manuelt
            </DialogDescription>
          </DialogHeader>
          
          {/* Inline Form */}
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Navn</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => handleFieldChange('author', e.target.value)}
                  placeholder="Kundens navn"
                  className="bg-gray-800 border-gray-600 text-white"
                  maxLength={100}
                  data-testid="review-author-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Kilde</Label>
                <Select
                  value={formData.source}
                  onValueChange={(value) => handleFieldChange('source', value)}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    {SOURCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Profilbilde (valgfritt)</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.avatar_url}
                  onChange={(e) => handleFieldChange('avatar_url', e.target.value)}
                  placeholder="Lim inn URL eller velg fra mediebibliotek"
                  className="flex-1 bg-gray-800 border-gray-600 text-white"
                  maxLength={500}
                  data-testid="review-avatar-input"
                />
                <Button
                  type="button"
                  onClick={() => setShowMediaPicker(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="open-media-picker-btn"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Velg bilde
                </Button>
              </div>
              {formData.avatar_url && (
                <div className="flex items-center gap-3 mt-2 p-2 bg-gray-800/50 rounded-lg">
                  <img 
                    src={formData.avatar_url} 
                    alt="Preview" 
                    className="w-12 h-12 rounded-full object-cover border-2 border-purple-500"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-300">Forhåndsvisning</span>
                    <button
                      type="button"
                      onClick={() => handleFieldChange('avatar_url', '')}
                      className="block text-xs text-red-400 hover:text-red-300 mt-1"
                    >
                      Fjern bilde
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Vurdering</Label>
              {renderStars(formData.rating, true, (rating) => handleFieldChange('rating', rating))}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Anmeldelse</Label>
              <Textarea
                value={formData.text}
                onChange={(e) => handleFieldChange('text', e.target.value)}
                placeholder="Skriv anmeldelsen her..."
                className="bg-gray-800 border-gray-600 text-white"
                rows={4}
                maxLength={2000}
                data-testid="review-text-input"
              />
              <p className="text-xs text-gray-500 text-right">{formData.text.length}/2000</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Dato (visning)</Label>
                <Input
                  value={formData.date_display}
                  onChange={(e) => handleFieldChange('date_display', e.target.value)}
                  placeholder="F.eks. '2 uker siden'"
                  className="bg-gray-800 border-gray-600 text-white"
                  maxLength={50}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Tjenestekategori</Label>
                <Select
                  value={formData.service_category || undefined}
                  onValueChange={(value) => handleFieldChange('service_category', value === "none" ? null : value)}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Velg kategori" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="none" className="text-white">Ingen kategori</SelectItem>
                    <SelectItem value="tungbil" className="text-white">Tungbil</SelectItem>
                    <SelectItem value="tilhenger" className="text-white">Tilhenger</SelectItem>
                    <SelectItem value="personbil" className="text-white">Personbil</SelectItem>
                    <SelectItem value="service" className="text-white">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.verified}
                  onChange={(e) => handleFieldChange('verified', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                />
                <span className="text-gray-300">Verifisert</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleFieldChange('featured', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                />
                <span className="text-gray-300">Utvalgt</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => handleFieldChange('published', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                />
                <span className="text-gray-300">Publisert</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)} className="border-gray-600 text-gray-300">
              Avbryt
            </Button>
            <Button onClick={handleCreate} disabled={actionLoading} className="bg-yellow-600 hover:bg-yellow-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Opprett'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-400" />
              Rediger anmeldelse
            </DialogTitle>
          </DialogHeader>
          
          {/* Inline Form for Edit */}
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Navn</Label>
                <Input
                  value={formData.author}
                  onChange={(e) => handleFieldChange('author', e.target.value)}
                  placeholder="Kundens navn"
                  className="bg-gray-800 border-gray-600 text-white"
                  maxLength={100}
                  data-testid="review-author-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Kilde</Label>
                <Select
                  value={formData.source}
                  onValueChange={(value) => handleFieldChange('source', value)}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    {SOURCE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Profilbilde (valgfritt)</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.avatar_url}
                  onChange={(e) => handleFieldChange('avatar_url', e.target.value)}
                  placeholder="Lim inn URL eller velg fra mediebibliotek"
                  className="flex-1 bg-gray-800 border-gray-600 text-white"
                  maxLength={500}
                  data-testid="review-avatar-input"
                />
                <Button
                  type="button"
                  onClick={() => setShowMediaPicker(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="open-media-picker-btn"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Velg bilde
                </Button>
              </div>
              {formData.avatar_url && (
                <div className="flex items-center gap-3 mt-2 p-2 bg-gray-800/50 rounded-lg">
                  <img 
                    src={formData.avatar_url} 
                    alt="Preview" 
                    className="w-12 h-12 rounded-full object-cover border-2 border-purple-500"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex-1">
                    <span className="text-sm text-gray-300">Forhåndsvisning</span>
                    <button
                      type="button"
                      onClick={() => handleFieldChange('avatar_url', '')}
                      className="block text-xs text-red-400 hover:text-red-300 mt-1"
                    >
                      Fjern bilde
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Vurdering</Label>
              {renderStars(formData.rating, true, (rating) => handleFieldChange('rating', rating))}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Anmeldelse</Label>
              <Textarea
                value={formData.text}
                onChange={(e) => handleFieldChange('text', e.target.value)}
                placeholder="Skriv anmeldelsen her..."
                className="bg-gray-800 border-gray-600 text-white"
                rows={4}
                maxLength={2000}
                data-testid="review-text-input"
              />
              <p className="text-xs text-gray-500 text-right">{formData.text.length}/2000</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Dato (visning)</Label>
                <Input
                  value={formData.date_display}
                  onChange={(e) => handleFieldChange('date_display', e.target.value)}
                  placeholder="F.eks. '2 uker siden'"
                  className="bg-gray-800 border-gray-600 text-white"
                  maxLength={50}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Tjenestekategori</Label>
                <Select
                  value={formData.service_category || undefined}
                  onValueChange={(value) => handleFieldChange('service_category', value === "none" ? null : value)}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Velg kategori" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-600">
                    <SelectItem value="none" className="text-white">Ingen kategori</SelectItem>
                    <SelectItem value="tungbil" className="text-white">Tungbil</SelectItem>
                    <SelectItem value="tilhenger" className="text-white">Tilhenger</SelectItem>
                    <SelectItem value="personbil" className="text-white">Personbil</SelectItem>
                    <SelectItem value="service" className="text-white">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.verified}
                  onChange={(e) => handleFieldChange('verified', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                />
                <span className="text-gray-300">Verifisert</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => handleFieldChange('featured', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                />
                <span className="text-gray-300">Utvalgt</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => handleFieldChange('published', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800"
                />
                <span className="text-gray-300">Publisert</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="border-gray-600 text-gray-300">
              Avbryt
            </Button>
            <Button onClick={handleUpdate} disabled={actionLoading} className="bg-blue-600 hover:bg-blue-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lagre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              Bekreft sletting
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette denne anmeldelsen?
            </DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="py-4">
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="text-white font-medium">{selectedReview.author}</p>
                <p className="text-gray-400 text-sm mt-1 line-clamp-2">{selectedReview.text}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} className="border-gray-600 text-gray-300">
              Avbryt
            </Button>
            <Button onClick={handleDelete} disabled={actionLoading} className="bg-red-600 hover:bg-red-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Slett'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              <span>Velg profilbilde fra mediebibliotek</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Klikk på et bilde for å velge det som profilbilde
            </DialogDescription>
          </DialogHeader>
          
          <AdminMediaLibrary 
            pickerMode={true} 
            onSelect={handleMediaSelect}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReviews;
